// "Client Forms" on a lead: has the client actually filled anything out?
// The answers live on the FILE (deal) the lead created, so this follows
// lead.sourceDealId and reads the same portalForms map the deal-detail panel
// uses — one rule, three screens that agree (utils/portalFormStatus.js).
// Each row opens the shared View Answers modal, so the team can read what the
// client wrote without leaving the drawer.
import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Circle, FileText } from "lucide-react";
import { getDealById } from "../../utils/firebasefunctions";
import { formsForRequest, applyOverridesAll } from "../../utils/portalForms";
import { loadPortalFormOverrides } from "../../utils/portalFormOverrides";
import {
  portalFormRows,
  portalFormProgress,
} from "../../utils/portalFormStatus";
import { formatDate } from "../../utils/helper";
import PortalFormAnswersModal from "../PortalFormAnswersModal";

const STATUS_UI = {
  submitted: {
    Icon: CheckCircle2,
    cls: "text-emerald-600",
    label: "Submitted",
  },
  started: { Icon: Clock, cls: "text-amber-500", label: "In progress" },
  "not-started": { Icon: Circle, cls: "text-slate-300", label: "Not started" },
};

export default function LeadPortalForms({ lead, side = "buyer" }) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFormId, setActiveFormId] = useState(null); // formId whose answers modal is open
  // Admin form overrides so the answers modal matches what the client saw.
  // {} until loaded and on any failure — the base forms then show unchanged.
  const [formOverrides, setFormOverrides] = useState({});

  useEffect(() => {
    let alive = true;
    if (!lead?.sourceDealId) {
      setDeal(null);
      return undefined;
    }
    setLoading(true);
    // getDealById THROWS when the file is missing (deleted, or an id that
    // never resolved) — treat that as "nothing to show", not a crash.
    getDealById(String(lead.sourceDealId))
      .then((d) => alive && setDeal(d))
      .catch(() => alive && setDeal(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [lead?.sourceDealId]);

  // loadPortalFormOverrides never throws (returns {} on missing/error).
  useEffect(() => {
    let alive = true;
    loadPortalFormOverrides().then((ov) => {
      if (alive) setFormOverrides(ov || {});
    });
    return () => {
      alive = false;
    };
  }, []);

  // No file yet = the forms were never sent, which is a normal state, not an
  // error: say so plainly instead of showing an empty checklist.
  // Deliberately not "above": in both drawers the action buttons live in the
  // bottom bar, so a direction would point the wrong way.
  if (!lead?.sourceDealId) {
    return (
      <p className="text-xs text-slate-400">
        No forms sent yet. Use the Send{" "}
        {side === "seller" ? "Seller" : "Buyer"} Forms button and their progress
        will show up here.
      </p>
    );
  }
  if (loading && !deal) {
    return <p className="text-xs text-slate-400">Checking the forms…</p>;
  }

  const forms = applyOverridesAll(
    formsForRequest({
      formType: deal?.formType,
      side,
      clients: Array.isArray(deal?.customers) ? deal.customers : [],
    }),
    formOverrides,
  );
  const rows = portalFormRows(deal, forms);
  const { submitted, total, allDone } = portalFormProgress(rows);

  const activeForm = activeFormId
    ? forms.find((f) => f.id === activeFormId) || null
    : null;
  const activeEntry = activeFormId
    ? (deal?.portalForms || {})[activeFormId]
    : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1.5">
          <FileText size={13} className="text-linkColor" />
          {submitted} of {total} submitted
        </span>
        {allDone && (
          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
            All done
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400">No forms on this file.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => {
            const ui = STATUS_UI[r.status] || STATUS_UI["not-started"];
            return (
              <li
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => setActiveFormId(r.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setActiveFormId(r.id);
                  }
                }}
                title="View this form's answers"
                className="flex items-center justify-between gap-2 text-sm py-1 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 rounded transition"
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <ui.Icon size={14} className={`${ui.cls} shrink-0`} />
                  <span className="text-slate-700 truncate">{r.title}</span>
                </span>
                <span className="text-[11px] text-slate-400 shrink-0">
                  {r.status === "submitted" && r.submittedAt
                    ? formatDate(r.submittedAt)
                    : ui.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {activeForm && (
        <PortalFormAnswersModal
          form={activeForm}
          entry={activeEntry}
          onClose={() => setActiveFormId(null)}
        />
      )}
    </div>
  );
}
